import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon, TrashIcon } from '@/icons';
import { StaticDataTypeLabel } from '@/services/staticDataTypeLabel/staticDataTypeLabel.types';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ManageStaticDataTypeLabelContainer } from '../ManageStaticDataTypeLabelContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;
type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  t: (key: string) => string,
  onOpen: OnOpenProps,
  openConfirmation: OpenConfirmationProps,
  mutateAsync: (staticDataLabelId: string) => Promise<unknown>,
  staticDataTypeId?: string,
): ColumnDef<StaticDataTypeLabel>[] {
  return [
    {
      accessorKey: 'sno',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'staticDataLabel',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('logsManagement.staticDataTypeLabel.page.table.staticDataLabel')}
        />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('staticDataLabel')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'staticDataDescription',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('logsManagement.staticDataTypeLabel.page.table.staticDataDescription')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary">{row.getValue('staticDataDescription')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => {
        const id = row.original.staticDataLabelId;
        return (
          <div className="flex gap-4 items-center">
            <Can rule={{ allOf: [PERMS.static.dataLabel.detail, PERMS.static.dataLabel.update] }}>
              <EditIcon
                size={16}
                className="text-foreground-caption hover:text-foreground-detail hover:cursor-pointer"
                onClick={() => {
                  onOpen({
                    title: 'logsManagement.staticDataTypeLabel.manageStaticDataTypeLabel.title',
                    okText: t('common.page.modal.submit'),
                    size: 'lg',
                    modalTitle: 'logsManagement.staticDataTypeLabel.manageStaticDataTypeLabel.title',
                    component: (
                      <ManageStaticDataTypeLabelContainer staticDataTypeId={staticDataTypeId} staticDataLabelId={id} />
                    ),
                  });
                }}
              />
            </Can>
            <Can rule={{ allOf: [PERMS.static.dataLabel.delete] }}>
              <TrashIcon
                className="text-destructive-text size-4 hover:cursor-pointer"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('logsManagement.staticDataTypeLabel.deleteStaticDataTypeLabel.description'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    onAction: async () => {
                      await mutateAsync(id);
                    },
                    onCancel: () => {},
                    variant: 'destructive',
                  });
                }}
              />
            </Can>
          </div>
        );
      },
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
